import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories, categoryColorMap } from '@/mocks/categories';

const heroImageUrl = 'https://readdy.ai/api/search-image?query=Dark%20dramatic%20entertainment%20studio%20backdrop%20with%20warm%20golden%20spotlight%20and%20abstract%20textures%2C%20deep%20brown%20and%20cream%20color%20palette%2C%20cinematic%20moody%20atmosphere%2C%20editorial%20photography%20style%2C%20soft%20shadow%20play%2C%20elegant%20minimalist%20composition&width=1600&height=800&seq=category-hero-generic&orientation=landscape';

const sideImageUrl = 'https://readdy.ai/api/search-image?query=Editorial%20still%20life%20with%20vintage%20newspapers%20and%20reading%20glasses%20on%20dark%20wooden%20desk%2C%20warm%20golden%20and%20deep%20brown%20tones%2C%20dramatic%20side%20lighting%2C%20investigative%20journalism%20aesthetic%2C%20rich%20textures%2C%20elegant%20composition&width=900&height=700&seq=category-side-generic&orientation=landscape';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);
  const colors = categoryColorMap[slug || ''] || { bg: 'bg-primary-100', text: 'text-primary-800', badge: 'bg-primary-500' };

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-800">分类不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 text-sm whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <main>
        {/* Category Hero */}
        <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-center">
          <div className="absolute inset-0">
            <img
              src={heroImageUrl}
              alt={category.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/50"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="w-full max-w-[1400px] mx-auto">
              <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                <div className="flex-1 text-center lg:text-left pt-20 lg:pt-0">
                  <span className={`inline-block px-3 py-1 text-[10px] font-semibold rounded-full ${colors.badge} text-background-50 whitespace-nowrap`}>
                    {category.name}
                  </span>
                  <h1 className="mt-4 text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 leading-tight tracking-tight">
                    {category.name}
                    <br />
                    <span className="italic text-accent-300">爆料合集</span>
                  </h1>
                  <p className="mt-4 text-sm md:text-base text-background-200/80 leading-relaxed max-w-lg mx-auto lg:mx-0">
                    {category.description}
                  </p>
                  <div className="flex flex-col sm:flex-row items-center gap-3 mt-6 justify-center lg:justify-start">
                    <span className="text-sm text-background-300">{categoryArticles.length} 篇爆料</span>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border border-background-300 text-background-50 text-sm font-medium hover:bg-white/10 transition-all duration-200 whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line text-sm"></i>
                      返回首页
                    </Link>
                  </div>
                </div>

                <div className="flex-1 w-full max-w-md lg:max-w-none">
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/3] lg:aspect-[5/4]">
                    <img
                      src={sideImageUrl}
                      alt={`${category.name} - 五一黑料网`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />
        </div>

        {/* Articles Grid */}
        <section className="pb-16 md:pb-20 bg-background-50">
          <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <h3 className="mt-4 text-lg font-semibold text-foreground-600">暂无相关爆料</h3>
                <p className="mt-2 text-sm text-foreground-400">该分类下暂时没有文章，我们会尽快更新内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 五一黑料网`,
            description: category.description,
            url: `https://www.wuyiheiliao.com/category/${slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '五一黑料网',
              url: 'https://www.wuyiheiliao.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.wuyiheiliao.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: category.name,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}